import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Film, Trash2, Loader2 } from "lucide-react";
import { adminApi } from "./adminApi";

const API = process.env.REACT_APP_BACKEND_URL;
const TYPES = "video/mp4,video/quicktime,video/webm";

const uploadVideo = async (file, onProgress) => {
  const { data } = await adminApi.post("/admin/video/init");
  const size = data.chunk_size;
  for (let start = 0; start < file.size; start += size) {
    await adminApi.put(`/admin/video/chunk/${data.upload_id}`, file.slice(start, start + size), { headers: { "Content-Type": "application/octet-stream" } });
    onProgress(Math.min(100, Math.round(((start + size) / file.size) * 100)));
  }
  return adminApi.post("/admin/video/complete", { upload_id: data.upload_id, content_type: file.type, filename: file.name });
};

export const VideoTab = () => {
  const input = useRef(null);
  const [url, setUrl] = useState(null);
  const [progress, setProgress] = useState(null);
  const load = () => adminApi.get("/media/doctor-video").then(({ data }) => setUrl(data?.url ?? null)).catch(() => setUrl(null));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (file.size > 250 * 1024 * 1024) return toast.error("Video troppo grande (max 250 MB)");
    setProgress(0);
    try { await uploadVideo(file, setProgress); await load(); toast.success("Video pubblicato in homepage"); }
    catch (e) { toast.error(e.response?.data?.detail || "Caricamento non riuscito"); }
    finally { setProgress(null); if (input.current) input.current.value = ""; }
  };
  const remove = async () => {
    try { await adminApi.delete("/admin/video"); setUrl(null); toast.success("Video rimosso"); }
    catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
  };

  return (
    <div data-testid="video-tab" className="max-w-3xl">
      <p className="text-sm text-[#526356]">Carica il video di presentazione del Dott. Mauro (consigliati 30-60 secondi, in verticale o orizzontale, formato MP4, max 250 MB). Compare nella sezione "Chi sono" della homepage; se non c'è nessun video, lo spazio resta nascosto.</p>
      <div className="mt-6 rounded-3xl bg-[#FCFBF7] border border-[#1B3B28]/10 p-5">
        {url ? <video src={`${API}${url}`} controls playsInline className="w-full max-h-[420px] rounded-2xl bg-black" data-testid="video-preview" />
          : <div className="grid place-items-center h-48 rounded-2xl bg-[#E6ECE4] text-sm text-[#526356]" data-testid="video-empty">Nessun video caricato</div>}
        {progress !== null && (
          <div className="mt-4" data-testid="video-progress">
            <div className="h-2 rounded-full bg-[#E6ECE4] overflow-hidden"><div className="h-full bg-[#3E6B48] transition-[width]" style={{ width: `${progress}%` }} /></div>
            <p className="text-xs text-[#526356] mt-2">Caricamento {progress}%… non chiudere la pagina.</p>
          </div>
        )}
        <div className="mt-5 flex gap-2">
          <input ref={input} type="file" accept={TYPES} className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="video-input" />
          <button onClick={() => input.current.click()} disabled={progress !== null} data-testid="video-upload-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] px-5 py-2.5 text-sm hover:bg-[#3E6B48] disabled:opacity-60">
            {progress !== null ? <Loader2 size={15} className="animate-spin" /> : <Film size={15} />} {url ? "Sostituisci video" : "Carica video"}
          </button>
          {url && <button onClick={remove} disabled={progress !== null} data-testid="video-remove-btn" aria-label="Rimuovi video"
            className="grid place-items-center w-10 h-10 rounded-full border border-[#1B3B28]/15 text-[#9A4B3C] hover:bg-[#F4E9E6]"><Trash2 size={15} /></button>}
        </div>
      </div>
    </div>
  );
};
